import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon } from 'lucide-react';
type RequestType = 'optreden' | 'muziekles' | 'workshop' | 'anders';
type Status = 'idle' | 'submitting' | 'success';
const types: {
  id: RequestType;
  label: string;
  placeholder: string;
}[] = [{
  id: 'optreden',
  label: 'Optreden',
  placeholder: 'Wat voor gelegenheid is het, hoeveel gasten verwacht u en waar is het?'
}, {
  id: 'muziekles',
  label: 'Muziekles',
  placeholder: 'Piano of gitaar? En heb je al wat ervaring?'
}, {
  id: 'workshop',
  label: 'Ukelele-workshop',
  placeholder: 'Voor welke groep, met hoeveel personen en rond welke datum?'
}, {
  id: 'anders',
  label: 'Iets anders',
  placeholder: 'Waar kan ik u mee helpen?'
}];
const isType = (v: string | null): v is RequestType => types.some(t => t.id === v);
export function ContactForm() {
  const [params] = useSearchParams();
  const initial = params.get('type');
  const [type, setType] = useState<RequestType>(isType(initial) ? initial : 'optreden');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const current = types.find(t => t.id === type) ?? types[0];
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Vul uw naam in';
    if (!/^\S+@\S+\.\S+$/.test(email)) next.email = 'Vul een geldig e-mailadres in';
    if (!message.trim()) next.message = 'Vertel kort waar u aan denkt';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const reset = () => {
    setName('');
    setEmail('');
    setPhone('');
    setDate('');
    setMessage('');
    setErrors({});
    setStatus('idle');
  };
  const field = 'w-full border-b border-ink/25 bg-transparent py-3 text-lg text-ink placeholder:text-ink/40 focus:border-ink focus:outline-none';
  const label = 'text-sm font-medium text-ink/70';
  return <AnimatePresence mode="wait" data-id="element-22" data-mp="3azn7fd82xryn">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} role="status" className="flex min-h-[420px] flex-col justify-center" data-id="element-23" data-mp="2x908bjyeq021">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ink text-amber" data-id="element-24" data-mp="2s95mhutho0nu">
            <CheckIcon className="h-6 w-6" data-id="element-25" data-mp="2eb77qob9a27t" />
          </span>
          <p className="mt-6 font-serif text-4xl text-ink" data-id="element-26" data-mp="2j990y22ebhx0">Dank u wel, {name.trim().split(' ')[0]}!</p>
          <p className="mt-3 max-w-sm text-lg text-ink/70" data-id="element-27" data-mp="1znfm0rummaru">
            Uw bericht is verstuurd. Ik neem binnen een werkdag contact met u op via {email}.
          </p>
          <button type="button" onClick={reset} className="mt-8 self-start text-sm font-semibold text-ink underline underline-offset-4 hover:no-underline" data-id="element-28" data-mp="1uq6z6dyj9ik9">
            Nog een bericht sturen
          </button>
        </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={false} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} className="space-y-8" data-id="element-29" data-mp="2im1j7vn0vhqh">
          <fieldset data-id="element-30" data-mp="2dmygihuilnlo">
            <legend className={label} data-id="element-31" data-mp="2dldtreefz8yd">Waarvoor neemt u contact op?</legend>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-32" data-mp="2sbbchetlr6g6">
              {types.map(t => <button key={t.id} type="button" aria-pressed={type === t.id} onClick={() => setType(t.id)} className={`whitespace-nowrap rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink ${type === t.id ? 'border-ink bg-ink text-paper' : 'border-ink/25 text-ink hover:border-ink'}`} data-id="element-33" data-mp="2ndjk6jp1pibg">
                  {t.label}
                </button>)}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-34" data-mp="bpo9g72737y4">
            <div data-id="element-35" data-mp="briv5nlaj439">
              <label htmlFor="name" className={label} data-id="element-36" data-mp="bs0yt5aa8kl7">Naam</label>
              <input id="name" autoComplete="name" value={name} onChange={e => setName(e.target.value)} className={field} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-err' : undefined} data-id="element-37" data-mp="gpkg6e2a1dhd" />
              {errors.name && <p id="name-err" className="mt-2 text-sm font-medium text-red-800" data-id="element-38" data-mp="pnctjcofub24">{errors.name}</p>}
            </div>
            <div data-id="element-39" data-mp="214m9vqc9smz7">
              <label htmlFor="email" className={label} data-id="element-40" data-mp="2143395s2ll0h">E-mail</label>
              <input id="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className={field} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-err' : undefined} data-id="element-41" data-mp="216fllk1qpxe2" />
              {errors.email && <p id="email-err" className="mt-2 text-sm font-medium text-red-800" data-id="element-42" data-mp="21hokdvb4aa1y">{errors.email}</p>}
            </div>
            <div data-id="element-43" data-mp="1xgmu43z6h8c4">
              <label htmlFor="phone" className={label} data-id="element-44" data-mp="1xf20qholnvfu">Telefoon <span className="text-ink/45" data-id="element-45" data-mp="1sibk9t8vuxtw">(optioneel)</span></label>
              <input id="phone" type="tel" autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} className={field} data-id="element-46" data-mp="1niymidgh46cj" />
            </div>
            {(type === 'optreden' || type === 'workshop') && <div data-id="element-47" data-mp="1eisc50g2nmhl">
                <label htmlFor="date" className={label} data-id="element-48" data-mp="1ejbw0mpa8ky9">Datum <span className="text-ink/45" data-id="element-49" data-mp="1ekmymj6zrmns">(optioneel)</span></label>
                <input id="date" type="date" value={date} onChange={e => setDate(e.target.value)} className={field} data-id="element-50" data-mp="3cxd9otbufl5x" />
              </div>}
          </div>

          <div data-id="element-51" data-mp="169e78s4q3ody">
            <label htmlFor="message" className={label} data-id="element-52" data-mp="168uqheqt761i">Bericht</label>
            <textarea id="message" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder={current.placeholder} className={`${field} resize-none`} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-err' : undefined} data-id="element-53" data-mp="1b0wc3r9va947" />
            {errors.message && <p id="message-err" className="mt-2 text-sm font-medium text-red-800" data-id="element-54" data-mp="1tzgvex5ioa7a">{errors.message}</p>}
          </div>

          <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-ink px-8 py-4 text-sm font-semibold text-paper transition-colors duration-150 hover:bg-stage disabled:opacity-70" data-id="element-55" data-mp="1fyo4emefbx8x">
            {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-56" data-mp="9h0xe73auhfd" />}
            {status === 'submitting' ? 'Versturen…' : 'Verstuur bericht'}
          </button>
        </motion.form>}
    </AnimatePresence>;
}